<template>
  <el-dialog
    :title="rateCard.name"
    visible
    top="5vh"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="$emit('update:isShow', false)"
  >
    <section v-loading="isLoading">
      <h1>
        Linked Accounts<br>
        <small>
          Accounts associated with this Rate Card
        </small>
      </h1>
      <el-table
        :data="linkedAccounts"
        size="mini"
        stripe
      >
        <el-table-column
          label="Account Name"
          prop="account"
          min-width="120px"
          show-overflow-tooltip
          :formatter="row => $_.get(row, 'account.name', '-')"
        />

        <el-table-column
          label="Start Date"
          prop="date_start"
          width="120px"
          :formatter="row => formatDate(row.date_start) || '-'"
        />

        <el-table-column
          label="End Date"
          prop="date_end"
          width="120px"
          :formatter="row => formatDate(row.date_end) || '-'"
        />

        <el-table-column
          label="Actions"
          align="right"
          width="80px"
        >
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="danger"
              @click.native="deleteLinkedAccount(scope.$index, scope.row)"
            >
              Delete
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="text-center pt-3"
        :current-page.sync="page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size.sync="perPage"
        layout="total, sizes, prev, pager, next"
        :total="total"
        @size-change="loadTable()"
        @current-change="loadTable()"
      />
    </section>

    <el-divider />

    <section>
      <h1>
        New Account<br>
        <small>
          Link a new Account to this Rate Card
        </small>
      </h1>
      <el-form
        ref="indexModelForm"
        v-loading="isSaving"
        :rules="rules"
        :model="indexModel"
        size="small"
      >
        <el-row :gutter="15">
          <el-col :span="12">
            <el-form-item
              label="Start Date"
              prop="date_start"
            >
              <el-date-picker
                v-model="indexModel.date_start"
                type="date"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                placeholder=""
                :picker-options="datePickerOption"
                class="w-100"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              label="End Date"
              prop="date_end"
            >
              <el-date-picker
                v-model="indexModel.date_end"
                type="date"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                placeholder=""
                :picker-options="datePickerOption"
                class="w-100"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col>
            <el-form-item
              label="Account"
              prop="account"
            >
              <el-select
                v-model="indexModel.account"
                filterable
                remote
                value-key="id"
                placeholder=""
                class="w-100"
                :remote-method="remoteMethodAccount"
                :loading="isLoadingAccounts"
              >
                <el-option
                  v-for="item in accounts"
                  :key="item.id"
                  :label="item.name"
                  :value="item"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </section>

    <span
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click.native="$emit('update:isShow', false)">
        Cancel
      </el-button>

      <el-button
        type="primary"
        :loading="isSaving"
        @click.native="attachAccount"
      >
        Link
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  props: {
    isShow: {
      type: Boolean,
      required: true
    },
    rateCard: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      linkedAccounts: [],
      indexModel: null,
      isFullscreen: false,
      isLoadingAccounts: false,
      accounts: [],
      page: 1,
      perPage: 5,
      total: 0,
      isLoading: false,
      isSaving: false,
      datePickerOption: {
        shortcuts: [{
          text: 'Today',
          onClick (picker) {
            picker.$emit('pick', new Date())
          }
        }, {
          text: 'Yesterday',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24)
            picker.$emit('pick', date)
          }
        }, {
          text: 'A week ago',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24 * 7)
            picker.$emit('pick', date)
          }
        }]
      },
      rules: {
        account: [
          { required: true, message: 'Account is required', trigger: 'change' }
        ]
      }
    }
  },
  created () {
    this.loadTable()
    this.indexModel = new this.$sdk.ratecard_index()
  },
  methods: {
    loadTable () {
      this.isLoading = true
      this.$sdk.ratecard_index.list(builder => {
        builder.include = ['account']
        builder.filters = { rate_card_index_group: this.rateCard.group.id }
        builder.page = this.page
        builder.limit = this.perPage
        builder.order = 'name:asc'
      })
        .then(res => {
          this.linkedAccounts = res
          this.linkedAccounts.map(a => {
            a.date_start = a.date_start === 'Invalid date' ? undefined : a.date_start
          })
          this.total = res.total
        })
        .finally(() => { this.isLoading = false })
    },
    attachAccount () {
      this.$refs.indexModelForm.validate((valid) => {
        if (valid) {
          if (!this.rateCard.group.id) {
            this.$message({ message: 'This rate card does not have a group', type: 'error' })
            return
          }
          this.isSaving = true
          this.indexModel.group = this.rateCard.group
          if (this.indexModel.date_start) this.indexModel.date_start = this.$moment(this.indexModel.date_start).format('YYYY-MM-DD 00:00:00')
          if (this.indexModel.date_end) this.indexModel.date_end = this.$moment(this.indexModel.date_end).format('YYYY-MM-DD 00:00:00')
          this.$sdk.ratecard_index.create(this.indexModel, builder => {
            builder.include = ['account']
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.indexModel = new this.$sdk.ratecard_index()
              this.$refs.indexModelForm.resetFields()
              this.loadTable()
            })
        }
      })
    },
    deleteLinkedAccount (index, row) {
      this
        .$confirm(
          `Are you sure you want to delete the <b>${row.account.name}</b> Account from the <b>${this.rateCard.name}</b> Rate Card?`,
          'Notice',
          {
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            dangerouslyUseHTMLString: true,
            showClose: false,
            type: 'warning'
          }
        )
        .then(() => {
          this.isLoading = true

          row
            .delete()
            .then(this.$message.success(`Successfully deleted ${row.account.name}`))
            .finally(this.loadTable)
        })
        .catch(() => {})
    },
    remoteMethodAccount (query) {
      this.isLoadingAccounts = true
      this.$sdk.account.list(builder => {
        builder.filters = { account_name: query, account_active: 1 }
      }).then(res => {
        this.accounts = res
      }).finally(() => { this.isLoadingAccounts = false })
    },
    formatDate (date) {
      if (date !== 'Unknown' && date !== null) {
        return this.$moment(date).format('DD/MM/YYYY')
      }
    }
  }
}
</script>
